<template>
  <main>
    <header>
      <img src="/assets/color-logo.svg" alt>
      <span>ID #{{ reportId }}</span>
    </header>
    <dl>
      <dt>Prodajno mjesto</dt>
      <dd v-if="seller">{{ seller.firstName }} {{ seller.lastName }}</dd>
      <dt>Za dan</dt>
      <dd>{{ formatDate(from || now) }}</dd>
    </dl>
    <h1>Blagajničko izvješće</h1>
    <p class="sale-period">Izvještaj sastavljen {{ formatDateTime(now) }}</p>
    <table>
      <thead>
        <tr>
          <th>Opis</th>
          <th />
          <th>Iznos (EUR)</th>
          <th>CoreEvent naknada (EUR)</th>
          <th>Ukupno (EUR)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Polog</td>
          <td />
          <td>100.00</td>
          <td>0.00</td>
          <td>100.00</td>
        </tr>
        <tr>
          <td>Ukupni primitak (novčanice)</td>
          <td />
          <td>{{ getTotalSoldAmount(cashReport, refundedCashReport) }}</td>
          <td>{{ getCoreEventFee(cashReport) }}</td>
          <td>{{ getTotalWithFee(cashReport, refundedCashReport) }}</td>
        </tr>
        <tr>
          <td>Ukupni primitak (kartice)</td>
          <td />
          <td>{{ getTotalSoldAmount(cardReport, refundedCardReport) }}</td>
          <td>{{ getCoreEventFee(cardReport) }}</td>
          <td>{{ getTotalWithFee(cardReport, refundedCardReport) }}</td>
        </tr>
        <tr>
          <td>Ukupni primitak (ostalo)</td>
          <td />
          <td>{{ getTotalSoldAmount(otherReport, refundedOtherReport) }}</td>
          <td>{{ getCoreEventFee(otherReport) }}</td>
          <td>{{ getTotalWithFee(otherReport, refundedOtherReport) }}</td>
        </tr>
      </tbody>
    </table>
    <p v-if="incompleteFiscalOrderCount" class="fiscal-note">
      Napomena: broj narudžbi s nepotpunim fiskalnim podacima izuzetih iz CoreEvent naknade:
      {{ incompleteFiscalOrderCount }}
    </p>
  </main>
</template>

<script>
import moment from 'moment'
import { GET_TICKET_SELLERS_REPORT } from '@/graphql/order.graphql'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { CoreEventFeeType, OrderStatusEnum } from '@/types'

export default {
  props: {
    from: {
      type: String,
      default: null,
    },
    to: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      orders: [],
      seller: null,
      totalSoldCount: 0,
      cashReport: [],
      cardReport: [],
      otherReport: [],
      refundedCashReport: [],
      refundedCardReport: [],
      refundedOtherReport: [],
      reportId: null,
      now: null,
    }
  },
  computed: {
    effectiveFrom() {
      return this.from || moment().startOf('day').toISOString()
    },
    effectiveTo() {
      return this.to || moment().endOf('day').toISOString()
    },
    // Orders that should have contributed a CoreEvent fee but whose fiscalisation
    // record is incomplete, deduplicated by order across all payment types.
    incompleteFiscalOrderCount() {
      const seen = []

      return [...this.cashReport, ...this.cardReport, ...this.otherReport].reduce((count, order) => {
        if (!this.isCoreEventFeeEligible(order) || this.hasCompleteFiscalReceipt(order)) {
          return count
        }
        if (order.orderId !== null && order.orderId !== undefined) {
          if (seen.includes(order.orderId)) {
            return count
          }
          seen.push(order.orderId)
        }

        return count + 1
      }, 0)
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY')
    },
    formatDateTime(date) {
      return moment(date).format('DD. MM. YYYY HH:mm')
    },
    getYear(date) {
      return moment(date).year()
    },
    sumReportAmount(reports) {
      return reports.reduce((sum, order) => {
        if (['KEKS10'].includes(order.buyer && order.buyer.paymentType)) {
          return sum + parseFloat(order.totalPrice) * 0.9
        }
        if (['TELEGRAM20', 'POPUST20'].includes(order.buyer && order.buyer.paymentType)) {
          return sum + parseFloat(order.totalPrice) * 0.8
        }
        if (['Popust40'].includes(order.buyer && order.buyer.paymentType)) {
          return sum + parseFloat(order.totalPrice) * 0.6
        }
        if (['Popust50'].includes(order.buyer && order.buyer.paymentType)) {
          return sum + parseFloat(order.totalPrice) * 0.5
        }
        if (
          ['CASH20', 'CARD20', 'CASH50', 'CARD50'].includes(order.buyer && order.buyer.paymentType)
        ) {
          if (order.buyer.paymentType.endsWith('20')) {
            return sum + parseFloat(order.totalPrice) * 0.8
          }

          return sum + parseFloat(order.totalPrice) * 0.5
        }

        return sum + parseFloat(order.totalPrice)
      }, 0)
    },
    getTotalSoldAmount(reports, refundedReports = []) {
      return (this.sumReportAmount(reports) - this.sumReportAmount(refundedReports)).toFixed(2)
    },
    isCoreEventFeeEligible(order) {
      return !(
        order.isTisakTicketSeller ||
        !order.fiscalisationInfo ||
        !order.fiscalisationInfo.useEventFeePerTicket ||
        order.fiscalisationInfo.fiscalisationError
      )
    },
    // A fiscalisation record can be partially populated (no full_receipt, or no
    // items on it). Dereferencing it would throw and blank the whole report, so
    // such orders are skipped and counted in incompleteFiscalOrderCount instead.
    hasCompleteFiscalReceipt(order) {
      const receipt = order.fiscalisationInfo && order.fiscalisationInfo['full_receipt']

      return Boolean(receipt) && Array.isArray(receipt.items)
    },
    sumCoreEventFee(reports) {
      const seen = []

      return reports.reduce((sum, order) => {
        if (!this.isCoreEventFeeEligible(order) || !this.hasCompleteFiscalReceipt(order)) {
          return sum
        }

        return order.fiscalisationInfo['full_receipt'].items.reduce((feeSum, i) => {
          if (!i.name || !i.name.includes(CoreEventFeeType.TICKET)) {
            return feeSum
          }
          const key = `${i.name}-${order.orderId}`
          if (seen.includes(key)) {
            return feeSum
          }
          seen.push(key)

          return feeSum + i.price / 100
        }, sum)
      }, 0)
    },
    getCoreEventFee(reports) {
      return this.sumCoreEventFee(reports).toFixed(2)
    },
    getTotalWithFee(reports, refundedReports = []) {
      return (
        parseFloat(this.getTotalSoldAmount(reports, refundedReports)) +
        this.sumCoreEventFee(reports)
      ).toFixed(2)
    },
  },
  apollo: {
    getTicketSellersReport: {
      query: GET_TICKET_SELLERS_REPORT,
      variables() {
        return {
          from: this.effectiveFrom,
          to: this.effectiveTo,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (loading || !data.getTicketSellersReport) {
          return
        }
        const { getTicketSellersReport } = data

        // Orders sold within the period: successful / refund requested, plus
        // orders created within the period that were later refunded (aligned
        // with the ticket seller report PDF).
        const soldOrders = getTicketSellersReport.filter(
          o =>
            [OrderStatusEnum.SUCCESSFUL, OrderStatusEnum.REFUND_REQUESTED].includes(o.status) ||
            (o.status === OrderStatusEnum.REFUNDED &&
              moment(o.orderCreated).isBetween(
                moment(this.effectiveFrom),
                moment(this.effectiveTo),
                undefined,
                '[]',
              )),
        )

        // Orders refunded within the period, subtracted from the totals.
        const refundedOrders = getTicketSellersReport.filter(
          o =>
            o.status === OrderStatusEnum.REFUNDED &&
            moment(o.orderRefunded).isBetween(
              moment(this.effectiveFrom),
              moment(this.effectiveTo),
              undefined,
              '[]',
            ),
        )

        const isCash = r => r.paymentType === 'CASH'
        const isCard = r => r.paymentType === 'CARD' && (!r.buyer || r.buyer.paymentType === 'CARD')
        const isOther = r =>
          ['OTHER', 'KEKS10', 'TELEGRAM20', 'POPUST20', 'Popust40'].includes(r.paymentType) ||
          ['KEKS10', 'TELEGRAM20', 'POPUST20', 'Popust40', 'Popust50'].includes(
            r.buyer && r.buyer.paymentType,
          ) ||
          ['CASH20', 'CARD20', 'CASH50', 'CARD50'].includes(r.buyer && r.buyer.paymentType)

        this.orders = soldOrders
        this.now = new Date()
        this.totalSoldCount =
          soldOrders.reduce((sum, order) => sum + order.count, 0) -
          refundedOrders.reduce((sum, order) => sum + order.count, 0)

        this.cashReport = soldOrders.filter(isCash)
        this.cardReport = soldOrders.filter(isCard)
        this.otherReport = soldOrders.filter(isOther)

        this.refundedCashReport = refundedOrders.filter(isCash)
        this.refundedCardReport = refundedOrders.filter(isCard)
        this.refundedOtherReport = refundedOrders.filter(isOther)
      },
    },
    me: {
      query: GET_LOGGED_USER,
      result({ data, loading }) {
        if (!loading && data) {
          const { me: user } = data
          this.seller = user
          const now = new Date()
          this.reportId = `${user.id}${now.getDate()}${now.getMonth() + 1}${
            now.getHours() * 60 + now.getMinutes()
          }`
        }
      },
    },
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body {
  height: 100%;
}

main {
  border-right: 0.11111in dashed #0277bd;
  height: 100%;
  margin-right: 1.333333in;
  padding: 0.33333in 0.22222in 0.33333in 0.44444in;
}

header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.33333in;
}

header img {
  height: 0.33333in;
}

header span {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.13889in;
  line-height: 120%;
  text-align: right;
  color: #979797;
}

dl {
  margin-bottom: 0.33333in;
}

dl dt {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.11111in;
  line-height: 120%;
  color: #979797;
  margin-bottom: 0.02777in;
}

dl dd {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.13889in;
  line-height: 120%;
  color: #666666;
}

dl dd + dt {
  margin-top: 0.11111in;
}

h1 {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 0.25in;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #333333;
}

.event-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 0.25in;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #979797;
  margin-bottom: 0.11111in;
}

.sale-period {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 0.16777in;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #979797;
  margin-bottom: 0.33333in;
}

table {
  width: 100%;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.13889in;
  line-height: 120%;
  color: #333333;
  border-collapse: collapse;
}

table thead {
  background: #e6f1f8;
}

table th {
  font-weight: normal;
  color: #0277bd;
}

table th,
table td {
  padding: 0.08333in 0.11111in;
}

table th:nth-child(-n + 2),
table tbody td:nth-child(-n + 2) {
  text-align: left;
}

table th:nth-child(n + 3),
table tbody td:nth-child(n + 3) {
  text-align: right;
}

table tfoot {
  background: #e6f1f8;
}

table tfoot td {
  color: #0277bd;
  text-align: right;
}

table tr:nth-child(even) {
  background: #f5f5f5;
}

.fiscal-note {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.11111in;
  line-height: 120%;
  color: #b71c1c;
  margin-top: 0.16666in;
}

.vat-info {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.11111in;
  line-height: 120%;
  color: #666666;
  margin-top: 0.33333in;
  text-align: right;
}
</style>
